import React, { useEffect } from 'react';
import { Box, Typography, CircularProgress, IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

import { PRESENTATION_STATE } from '../../../Presentaciones/consts';
import { useTranslation } from 'react-i18next';

import { useLazyGetPresentacionRGRLPDFbyIdQuery } from '../../../../services/presentacionesApi';
import { useSnackbar } from 'notistack';
import { useAppDispatch } from '../../../../hooks/reduxHooks';
import useDownloadFile from '../../../../hooks/useDownloadFile';
import { showAlert } from '../../../../features/slices/applicationSlice';
import ActivitySummaryChip from './ActivitySummaryChip';
import { VISIT_TYPE_DISPLAY_NAME_OVERRIDES, VisitTypeId } from '../../consts/visitTypeDisplayNames';

const RGRL_DISPLAY_NAME = VISIT_TYPE_DISPLAY_NAME_OVERRIDES[VisitTypeId.RGRL];

interface RGRLSectionProps {
    rgrlPresentaciones: any[];
    loadingPresentaciones: boolean;
    allPresentationsCancelled: boolean;
    handleOpenDialogRGRL: () => void;
    handleCardClickRGRL: (id: number, nombre: string) => void;
    confirmCancelPresentation: (id: number) => void;
    handleEditRGRL: (presentacion: any) => void;
    isFailedVisit: boolean;
    onRgrlStatusChange?: (isRecibido: boolean) => void;
    isReadOnly: boolean;
    // Los autoseguros presentan RRL en lugar de RGRL: define el nombre del PDF descargado.
    esAutoseguro?: boolean;
}

const RGRLSection: React.FC<RGRLSectionProps> = ({
    rgrlPresentaciones,
    loadingPresentaciones,
    allPresentationsCancelled,
    handleOpenDialogRGRL,
    handleCardClickRGRL,
    confirmCancelPresentation,
    handleEditRGRL,
    onRgrlStatusChange,
    isFailedVisit,
    isReadOnly,
    esAutoseguro = false
}) => {
    const { t } = useTranslation('visitas');
    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const [getRgrlPdf] = useLazyGetPresentacionRGRLPDFbyIdQuery();
    const { downloadFile } = useDownloadFile();
    const firstRgrlPresentation = rgrlPresentaciones?.[0];
    const visibleRgrlPresentation =
        firstRgrlPresentation?.idEstado !== 6 ? firstRgrlPresentation : null;

    const validRgrlStates = [
        PRESENTATION_STATE.RECIBIDO,
        PRESENTATION_STATE.EN_REVISION,
        PRESENTATION_STATE.PENDIENTE_VISITA,
        PRESENTATION_STATE.PRESENTADO,
        PRESENTATION_STATE.VISADO
    ];

    const isRgrlRecibido = Boolean(
        firstRgrlPresentation && validRgrlStates.includes(firstRgrlPresentation.idEstado)
    );

    useEffect(() => {
        onRgrlStatusChange?.(isRgrlRecibido);
    }, [isRgrlRecibido, onRgrlStatusChange]);

    const handleDownloadRgrl = async (idPresentacion: number) => {
        try {
            dispatch(
                showAlert({
                    icon: 'info',
                    message: t('alerts.downloadRgrl.generating'),
                    loading: true
                })
            );

            const response = await getRgrlPdf({
                idPresentacion,
                typeId: 2
            }).unwrap();

            if (!response?.data?.presignedUrls?.length) {
                throw new Error(String(t('alerts.downloadRgrl.noFileReceived')));
            }

            // fetch+blob, igual que el export de RGRL. Navegar a la presignedUrl con un <a>
            // no descargaba nada cuando la generación del PDF tardaba unos segundos.
            const url = response.data.presignedUrls[0];
            const prefijo = esAutoseguro ? 'RRL' : 'RGRL';
            const guardado = await downloadFile(url, '.pdf', `${prefijo}_${idPresentacion}`);
            if (!guardado) {
                throw new Error(String(t('alerts.downloadRgrl.error')));
            }

            dispatch({ type: 'application/hideAlert' });
            enqueueSnackbar(t('alerts.downloadRgrl.success'), { variant: 'success' });
        } catch (error: any) {
            dispatch({ type: 'application/hideAlert' });

            enqueueSnackbar(error?.message || t('alerts.downloadRgrl.error'), { variant: 'error' });
        }
    };

    if (loadingPresentaciones) {
        return (
            <Box display="flex" alignItems="center" gap={2}>
                <CircularProgress size={24} />
            </Box>
        );
    }

    if (!visibleRgrlPresentation || allPresentationsCancelled) {
        return (
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                width="100%"
                sx={{ borderBottom: '1px solid #ccc', padding: '12px 8px' }}
            >
                <Box display="flex" alignItems="center" gap={2}>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, textTransform: 'uppercase' }}>
                        {RGRL_DISPLAY_NAME}
                    </Typography>
                    <ActivitySummaryChip status="PENDING" />
                </Box>

                <IconButton disabled={isReadOnly} onClick={handleOpenDialogRGRL}>
                    <EditIcon />
                </IconButton>
            </Box>
        );
    }

    const showEditIcon = ![2, 3, 4, 5].includes(visibleRgrlPresentation.idEstado);
    const isRgrlDone = validRgrlStates.includes(visibleRgrlPresentation.idEstado);

    return (
        <Box display="flex" width="100%" flexDirection="column" gap={2}>
            <Box
                key={visibleRgrlPresentation.id}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                width="100%"
                sx={{ borderBottom: '1px solid #ccc', padding: '12px 8px' }}
            >
                <Box display="flex" alignItems="center" gap={2}>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, textTransform: 'uppercase' }}>
                        {RGRL_DISPLAY_NAME}
                    </Typography>
                    <ActivitySummaryChip status={isRgrlDone ? 'DONE' : 'PENDING'} />
                </Box>

                <Box display="flex" gap={1}>
                    {visibleRgrlPresentation.idEstado === PRESENTATION_STATE.BORRADOR ? (
                        <IconButton
                            disabled={isReadOnly}
                            onClick={() => handleEditRGRL(visibleRgrlPresentation)}
                        >
                            <EditIcon />
                        </IconButton>
                    ) : (
                        showEditIcon && (
                            <IconButton
                                disabled={isReadOnly}
                                onClick={() =>
                                    handleCardClickRGRL(
                                        visibleRgrlPresentation.tipo.id,
                                        visibleRgrlPresentation.tipo.nombre
                                    )
                                }
                            >
                                <EditIcon />
                            </IconButton>
                        )
                    )}

                    {/* Misma condición que el chip "Realizado" (SMP-579-CA-1): la presentación
                        puede pasar de RECIBIDO a PRESENTADO al entrar en un archivo SRT, y el
                        endpoint del PDF no restringe por estado. */}
                    {isRgrlDone && (
                        <IconButton
                            onClick={() => handleDownloadRgrl(visibleRgrlPresentation.id)}
                            title={String(t('table.actions.downloadRgrl'))}
                        >
                            <FileDownloadIcon />
                        </IconButton>
                    )}

                    <IconButton
                        disabled={isReadOnly}
                        onClick={() => confirmCancelPresentation(visibleRgrlPresentation.id)}
                    >
                        <DeleteIcon />
                    </IconButton>
                </Box>
            </Box>
        </Box>
    );
};

export default RGRLSection;
